import React, { useEffect } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { isNull } from 'lodash';
import DocumentUpload from '../../components/Documents/DocumentUpload';
import DocumentList from '../../components/Documents/DocumentList';
import ProcessingDocumentsList from '../../components/Documents/ProcessingDocumentsList';
import { useDocumentPermissions } from '../../hooks/useDocumentPermissions';
import {
  deleteDocument,
  fetchAllDocuments,
  uploadAndTrackDocuments,
  UploadDocumentsOutcome,
} from '../../store/documents/actionCreators';
import { getDashboardUser } from '../../store/dashboardUser/actionCreators';
import { AppDispatch, RootState } from '../../store';
import { getCookie } from '../../shared/utils';

const DocumentsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { dashboardUserInfo } = useSelector(
    (state: RootState) => state.dashboardUser,
  );
  const { uploadedDocuments, isUploading, isLoadingDocuments } = useSelector(
    (state: RootState) => state.documents,
  );

  const {
    documents,
    addDocuments,
    removeDocument,
    updatePermissions,
    removeDocumentsByFilenames,
  } = useDocumentPermissions();

  const token = getCookie('access_token') || '';
  const customerId = dashboardUserInfo?.customer_id ?? 0;
  const allDocumentsHavePermissions = documents.every(
    (document) => document.permissions.length > 0,
  );

  useEffect(() => {
    if (isNull(dashboardUserInfo)) {
      dispatch(getDashboardUser());
    }
  }, [dashboardUserInfo, dispatch]);

  useEffect(() => {
    if (!token || !customerId) {
      return;
    }

    dispatch(fetchAllDocuments(token, customerId));
  }, [customerId, dispatch, token]);

  const handleDeleteDocument = async (
    documentId: string,
    documentCustomerId: number,
  ) => {
    if (!token || !documentCustomerId) {
      toast.error('Unable to delete document. User session is incomplete.');
      return;
    }

    await dispatch(deleteDocument(documentId, token, documentCustomerId));
  };

  const handleUpload = async () => {
    if (documents.length === 0) {
      return;
    }

    if (!token || !customerId) {
      toast.error('Unable to upload documents. User session is incomplete.');
      return;
    }

    const payload = documents.map((doc) => ({
      file: doc.file,
      permissions: doc.permissions,
    }));

    try {
      const outcome = (await dispatch(
        uploadAndTrackDocuments(payload, token, customerId),
      )) as UploadDocumentsOutcome;

      removeDocumentsByFilenames(outcome.uploadedFilenames);
    } catch {
    }
  };

  return (
    <Box sx={{ p: 4, width: '100%', boxSizing: 'border-box' }}>
      <Typography variant="h5" sx={{ mb: 4, fontWeight: 600 }}>
        Document upload
      </Typography>

      <DocumentUpload onFilesSelected={addDocuments} />

      {documents.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <DocumentList
            documents={documents}
            onRemove={removeDocument}
            onUpdatePermissions={updatePermissions}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleUpload}
              disabled={isUploading || !allDocumentsHavePermissions}
              sx={{
                px: 4,
                py: 1,
                borderRadius: '24px',
                textTransform: 'none',
                minWidth: '120px',
                backgroundColor: '#D1C4E9',
                color: '#4A148C',
                '&:hover': {
                  backgroundColor: '#B39DDB',
                },
              }}
            >
              {isUploading ? <CircularProgress size={24} /> : 'Upload'}
            </Button>
          </Box>
        </Box>
      )}

      <ProcessingDocumentsList
        documents={uploadedDocuments}
        isLoading={isLoadingDocuments}
        onDelete={handleDeleteDocument}
      />
    </Box>
  );
};

export default DocumentsPage;
